import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Paperclip, Upload, Trash2, FileText, Loader2, RefreshCw } from "lucide-react";

// Upload/elenco allegati collegati a un record (modulo + refId) via object storage.
export function FileUpload({ modulo, refId, label = "Allegati", accept = ".pdf,.png,.jpg,.jpeg,.webp,.docx,.xlsx", compact = false, readOnly = false, testid = "file-upload", onChange }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();
  const replaceId = useRef(null); // id del file da sostituire (rimosso dopo upload riuscito)

  const load = () => {
    if (!refId) { setFiles([]); return; }
    api.get(`/files/by-ref/${modulo}/${refId}`).then((r) => { setFiles(r.data); onChange && onChange(); }).catch(() => setFiles([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [modulo, refId]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) { replaceId.current = null; return; }
    if (!refId) { toast.error("Salva prima il record, poi allega i file."); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("modulo", modulo);
    fd.append("ref_id", refId);
    const toReplace = replaceId.current;
    setBusy(true);
    try {
      await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (toReplace) {
        try { await api.delete(`/files/${toReplace}`); } catch { /* nuovo file già caricato */ }
        toast.success("Allegato sostituito");
      } else {
        toast.success("File caricato");
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      replaceId.current = null;
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const pickNew = () => { replaceId.current = null; inputRef.current?.click(); };
  const pickReplace = (id) => { replaceId.current = id; inputRef.current?.click(); };

  const del = async (id) => {
    if (!window.confirm("Rimuovere l'allegato?")) return;
    try { await api.delete(`/files/${id}`); load(); } catch { toast.error("Errore"); }
  };

  return (
    <div data-testid={testid} className={compact ? "" : "rounded-lg border border-slate-200 p-3"}>
      {!compact && <div className="flex items-center gap-1.5 text-xs font-medium text-slate-500 mb-2"><Paperclip className="h-3.5 w-3.5" /> {label}</div>}
      <div className="space-y-1.5">
        {files.map((f) => (
          <div key={f.id} className="flex items-center justify-between gap-2 text-sm bg-slate-50 rounded px-2 py-1.5" data-testid={`file-item-${f.id}`}>
            <a href={`${API}/files/${f.id}/download`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-slate-700 hover:text-brand-blue truncate">
              <FileText className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{f.original_filename}</span>
            </a>
            <div className={`flex items-center gap-1 shrink-0 ${readOnly ? "hidden" : ""}`}>
              <button onClick={() => pickReplace(f.id)} disabled={busy} data-testid={`file-replace-${f.id}`} title="Sostituisci con un altro file" className="inline-flex items-center gap-1 text-slate-500 hover:text-brand-blue hover:bg-slate-100 rounded px-1.5 py-0.5"><RefreshCw className="h-3.5 w-3.5" /><span className="text-xs">Sostituisci</span></button>
              <button onClick={() => del(f.id)} data-testid={`file-delete-${f.id}`} title="Rimuovi questo allegato" className="inline-flex items-center gap-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded px-1.5 py-0.5"><Trash2 className="h-3.5 w-3.5" /><span className="text-xs">Rimuovi</span></button>
            </div>
          </div>
        ))}
        {files.length === 0 && <div className="text-xs text-slate-400">Nessun allegato.</div>}
      </div>
      <input ref={inputRef} type="file" accept={accept} onChange={onPick} className="hidden" data-testid={`${testid}-input`} />
      {!readOnly && (
      <Button type="button" size="sm" variant="outline" disabled={busy || !refId} onClick={pickNew} className="mt-2 h-8 text-xs" data-testid={`${testid}-btn`}>
        {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />} Carica file
      </Button>
      )}
    </div>
  );
}
